import { useEffect, useMemo, useRef, useState } from "react";
import { placeKey } from "../../core/ops";
import { C } from "../../core/schema";
import { useTranslation } from "react-i18next";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import type { FilterAst } from "../../core/filter";
import { groupsOf } from "../../core/structure";
import { useSession } from "../../app/session";
import { FILTER_SCHEMA, visibleTasks } from "../shared";
import { colorKeys, css, hasWebGL } from "../eisenhower/geometry";
import type { MapApi, MapPoint } from "./mapScene";
import { parseCenter } from "./center";

export interface MapConfig {
  filter?: FilterAst;
  /** "lat, lon"; empty = fit to the tasks */
  center?: string;
  zoom?: number;
  cluster?: boolean;
}

const DEFAULT_TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

/**
 * Tasks on a map (spec §9.7 `map`): every task with a `location` (a geo property) is a point in its
 * group's colour; click opens the task. The list of located tasks is always in the DOM, so the widget
 * works without WebGL and for screen readers.
 */
export function MapView({ config, ctx }: ViewProps<MapConfig>) {
  const { t } = useTranslation();
  const tileUrl = useSession((s) => s.session?.features?.tileUrl) || DEFAULT_TILES;
  const minute = Math.floor(ctx.now / 60_000) * 60_000;
  const raw = useDocSelector(
    ctx.handle,
    (doc) => {
      const v = visibleTasks(doc, config.filter, minute);
      const out: { id: string; title: string; lat: number; lon: number; key: string }[] = [];
      // where a task is: its place ("Remind me when I'm at"), every place of a place list, or the points found for a
      // kind of place (UI cleanup U3: the separate Location field, read only by this widget, is gone)
      const settings = doc.getMap(C.settings).toJSON() as {
        places?: Record<string, { name?: string; lat?: number; lon?: number }>;
        placeLists?: Record<string, { name?: string; placeIds?: string[] }>;
      };
      const places = settings.places ?? {};
      const lists = settings.placeLists ?? {};
      const byName = (n: string) =>
        Object.entries(places).find(([, p]) => placeKey(String(p?.name ?? "")) === placeKey(n))?.[0];
      for (const task of v.tasks) {
        const at = task.props.atPlace;
        const spots: [number, number][] = [];
        if (typeof at === "string" && at) {
          const pid = at in places ? at : byName(at);
          const list = !pid
            ? Object.entries(lists).find(
                ([id, l]) => id === at || placeKey(String(l?.name ?? "")) === placeKey(at),
              )
            : undefined;
          for (const id of pid ? [pid] : (list?.[1].placeIds ?? [])) {
            const p = places[id];
            if (p && typeof p.lat === "number" && typeof p.lon === "number") spots.push([p.lat, p.lon]);
          }
        }
        const points = task.props["atPlace.points"];
        if (Array.isArray(points))
          for (const pt of points.slice(0, 50))
            if (Array.isArray(pt) && typeof pt[0] === "number" && typeof pt[1] === "number")
              spots.push([pt[0], pt[1]]);
        const key = groupsOf(v.snapshot, task.id).sort()[0] ?? "";
        spots.forEach(([lat, lon]) => out.push({ id: task.id, title: task.title, lat, lon, key }));
      }
      return out;
    },
    [config, minute],
  );
  const [webgl] = useState(hasWebGL);
  const host = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<MapApi | null>(null);
  const center = parseCenter(config.center);
  const cluster = config.cluster ?? true;
  const zoom = config.zoom;
  const lat = center?.[0];
  const lon = center?.[1];

  useEffect(() => {
    if (!webgl) return;
    let alive = true;
    let made: MapApi | null = null;
    void import("./mapScene").then(({ createMap }) => {
      const el = host.current;
      if (!alive || !el) return;
      made = createMap(el, {
        tileUrl,
        center: lat !== undefined && lon !== undefined ? [lat, lon] : undefined,
        zoom,
        cluster,
        reducedMotion: document.documentElement.dataset.reducedMotion === "true",
        surface: css("--surface"),
        clusterColor: css("--chart-seq"),
        attribution: t("views.map.attribution"),
      });
      setMap(made);
    });
    return () => {
      alive = false;
      made?.dispose();
      setMap(null);
    };
  }, [webgl, tileUrl, lat, lon, zoom, cluster, t]);

  const points = useMemo((): MapPoint[] => {
    const list = raw ?? [];
    const keys = colorKeys(list.map((p) => p.key));
    return list.map((p) => ({ ...p, color: webgl ? css(keys.get(p.key) ?? "--chart-other") : "" }));
  }, [raw, webgl]);

  useEffect(() => {
    map?.update(points, ctx.openTask);
  }, [map, points, ctx.openTask]);

  return (
    <div
      className="kss-surface relative flex h-full flex-col overflow-hidden"
      data-testid="map-view"
      data-points={points.length}
    >
      {webgl ? (
        <div ref={host} className="min-h-0 flex-1" data-testid="map-canvas" />
      ) : (
        <p className="p-3 text-[12.5px] text-neutral-500">{t("views.map.noWebgl")}</p>
      )}
      {points.length === 0 && (
        <p className="absolute bottom-2 left-2 rounded-sm bg-[var(--surface)] px-2 py-1 text-[12px] text-neutral-500">
          {t("views.map.empty")}
        </p>
      )}
      <ul
        className={webgl ? "sr-only" : "overflow-auto px-3 pb-3 text-[13px]"}
        aria-label={t("views.map.label")}
      >
        {points.map((p, i) => (
          <li key={`${p.id}-${i}`}>
            <button type="button" className="hover:text-accent" onClick={() => ctx.openTask(p.id)}>
              {t("views.map.point", { title: p.title, lat: p.lat.toFixed(4), lon: p.lon.toFixed(4) })}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

registerViewType<MapConfig>({
  id: "map",
  label: "views.map.label",
  icon: "map",
  level: "power",
  defaultConfig: { cluster: true },
  configSchema: {
    type: "object",
    properties: {
      center: { type: "string", title: "config.center" },
      zoom: { type: "number", title: "config.zoom" },
      cluster: { type: "boolean", title: "config.cluster" },
      filter: FILTER_SCHEMA,
    },
  },
  Component: MapView,
  minSize: { w: 4, h: 5 },
});
